/*eslint-disable camelcase*/
import React, { useContext, useEffect, useRef, useState } from 'react';
import { getConfig } from '../../utils/config';
import { parseCookies } from '../../utils/parseCookies';

// ad components
import { TakeoverContext, TrackingContext } from '../../contexts';
import Ads from '../../components/Ads/Ads';
import AdUnit from '../../components/Ads/AdUnit';
import { MAX_AGE_SHORT } from '../../constants/feeds';
import { CLUSTER } from '../../constants';

// apis
import { getSiteHeader } from '../../upstreams/bf-header-ui';
import { getPageByName } from '../../upstreams/feed-api';
import { getThemeStyles, normalizeZones } from '../../utils/data';
import { getFeedPageSurrogateKeys } from '../../utils/surrogate-keys';
import { getPageMeta } from '../../utils/meta';
import { resolveAuthorization } from '../../utils/auth';
import {
  getQuizBookmarkedPostIds as getClientQuizBookmarkedPostIds,
  QUIZ_BOOKMARKS_UPDATED_EVENT,
} from '../../upstreams/bookmarks-api-client';
import setResponseHeaders from '@buzzfeed/headers';

// other components
import HomepageSiteHeader from '../../components/SiteHeader';
import CustomHead from '../_head';
import {QuizzLogo} from '../../icons/QuizzLogo';
import QuizNav from '../../components/QuizNav';
import QuizSplash from '../../components/QuizSplash';
import FeedContent from '../../components/FeedContent';
import FeedPageWrapper from '../../components/FeedPageWrapper';
import { BackToTop } from '@buzzfeed/react-components';

import styles from './quizzes.module.scss';

const QUIZ_BASE_NAME = 'custom__bf__en-us__quizzes';

export function QuizSubpage({
  header,
  metadata = {},
  pageConfig,
  path,
  zones,
  userGeo,
  slug = [],
}) {
  const takeover = useContext(TakeoverContext);
  const themeCss = getThemeStyles(metadata?.theme);
  const inlineCss = takeover?.inlineCss ? takeover.inlineCss : '' + themeCss;
  const { page_edition } = useContext(TrackingContext);
  const [resolvedBookmarkedPostIds, setResolvedBookmarkedPostIds] = useState([]);

  useEffect(() => {
    let isMounted = true;
    let latestRequestId = 0;

    const loadBookmarkedPostIds = () => {
      const requestId = ++latestRequestId;

      getClientQuizBookmarkedPostIds()
        .then((nextBookmarkedPostIds) => {
          if (isMounted && requestId === latestRequestId) {
            setResolvedBookmarkedPostIds(nextBookmarkedPostIds);
          }
        })
        .catch(() => {
          if (isMounted && requestId === latestRequestId) {
            setResolvedBookmarkedPostIds([]);
          }
        });
    };

    loadBookmarkedPostIds();

    window.addEventListener(QUIZ_BOOKMARKS_UPDATED_EVENT, loadBookmarkedPostIds);

    return () => {
      isMounted = false;
      window.removeEventListener(QUIZ_BOOKMARKS_UPDATED_EVENT, loadBookmarkedPostIds);
    };
  }, []);

  const bookmarkedPostIdsSet = React.useMemo(
    () => new Set(resolvedBookmarkedPostIds.map(id => String(id))),
    [resolvedBookmarkedPostIds]
  );

  const {
    main_tabs: tabs,
    splash,
    content
  } = zones;

  const subTabs = zones?.[`${slug[0]}_subtabs`] || {};

  // Tracking info calculations
  let zoneRenderOrder = 0;
  const tabNavTrackingInfo = {
    unit_type: 'nav',
    unit_name: `${tabs.name}|${zoneRenderOrder}`,
  }
  zoneRenderOrder = zoneRenderOrder + !!tabs?.items?.length; // 0 or 1
  const subTabNavTrackingInfo = {
    unit_type: 'nav',
    unit_name: `${subTabs.name}|${zoneRenderOrder}`,
  }
  zoneRenderOrder = zoneRenderOrder + !!subTabs?.items?.length;
  const splashTrackingInfo = {
    unit_type: 'feed',
    unit_name: `${splash.name}|${zoneRenderOrder}`,
  }
  zoneRenderOrder = zoneRenderOrder + !!splash?.items?.length;
  const contentTrackingInfo = {
    unit_type: 'feed',
    unit_name: `${content.name}|${zoneRenderOrder}`,
  }

  const getTrackingDataWithPosition = position => ({ position_in_unit: position });
  const sharedIndexRef = useRef({});

  return (
    <FeedPageWrapper userGeo={userGeo}>
      <Ads edition={page_edition} pagePath={path} pageName="quizzes">
        <AdUnit type="toolbar" />
        <AdUnit className="Ad--topic" type="awareness" />
        <HomepageSiteHeader {...header} />
        <CustomHead page="quizzes" pageConfig={pageConfig} inlineCss={inlineCss} userGeo={userGeo} />
        <main className="feed-content-area quizzes-page" style={takeover?.style}>
          <h1 className={styles.pageTitle}>
            <span className="screen-reader-only">Quizzes</span>
            <QuizzLogo aria-hidden="true" focusable="false" />
          </h1>
          <QuizNav
            data={tabs}
            trackingData={tabNavTrackingInfo}
          />
          <FeedContent
            className="tabContent"
            data={content}
            componentIndex={2}
            getTrackingDataWithPosition={getTrackingDataWithPosition}
            inlineAdConfigPrefix="promo-inline"
            isTrackable={true}
            maxItemsPerSequence={0}
            pageName="quizzes"
            sidebarAdPositionPrefix="sidebar"
            sidebarAdPositionSuffix="-bp"
            sidebarAdUsesBigstory={false}
            sharedIndexRef={sharedIndexRef}
            lazyLoadMore={true}
            trackingData={contentTrackingInfo}
            bookmarkedPostIds={bookmarkedPostIdsSet}
          >
            {!!subTabs?.items?.length && (
              <QuizNav
                data={subTabs}
                secondaryNav={true}
                trackingData={subTabNavTrackingInfo}
              />
            )}
            <QuizSplash
              data={splash}
              className={styles.splashSubPage}
              trackingData={splashTrackingInfo}
              bookmarkedPostIds={bookmarkedPostIdsSet}
            />
          </FeedContent>
          <BackToTop className={styles.backToTop} />
        </main>
      </Ads>
    </FeedPageWrapper>
  );
}


export async function getServerSideProps({ req, res, query }) {
  const edition = 'en-us';
  const staticProps = {
    namespacesRequired: ['common'],
  };

  const skipCache = CLUSTER !== 'prod';

  const slug = query?.slug || [];

  try {
    const name = `${QUIZ_BASE_NAME}_${slug.join('_')}`;
    const [
      header, // Fetch Error --> 500
      feedData,
      // eslint-disable-next-line no-undef
    ] = await Promise.all([
      getSiteHeader({ edition }),
      getPageByName({
        name,
        skipCache,
        tags: ['type:page', 'upstream:feed_api', 'page_type:quiz_page']
      })
    ]);

    if (!feedData || (feedData instanceof Object && !Object.keys(feedData).length)) {
      return {
        notFound: true,
      };
    }

    resolveAuthorization({ req, res }, feedData.authorization);

    const customKeys = getFeedPageSurrogateKeys({ feedPage: feedData, cluster: CLUSTER });

    const zones = normalizeZones(feedData.zones, { originTag: '' });

    const meta = getPageMeta({
      ...feedData,
      pageName: 'quizzes',
    });

    const cookies = parseCookies(req);
    const userGeo = cookies['bf-geo-country'] || '';

    // disable ads for buzzfeed plus members
    const adsDisabled = req.get('X-BFMem-Type') === 'buzzfeed_plus';

    setResponseHeaders({
      req,
      res,
      customKeys,
      /**
       * A max-age is defined only if served on prod and there is no basic auth required. Otherwise
       * max-age will be `0`.
       * @todo make longer once full event driven purging is set up
       */
      maxAge: CLUSTER === 'prod' && !feedData.authorization ? MAX_AGE_SHORT : 0,
      edition,
    });

    return {
      props: {
        ...staticProps,
        adsDisabled,
        header,
        userGeo,
        metadata: feedData.metadata,
        pageConfig: {
          pageType: 'quizzes',
          ...getConfig({ page: 'quizzes', edition, meta, path: req.path })
        },
        path: req.path,
        zones,
        tracking: {
          unit_type: 'feed',
          unit_name: 'main',
          context_page_type: 'feed',
          context_page_id: 6,
          destination: 'buzzfeed',
          page_edition: 'en-us',
        },
        slug,
      },
    };
  } catch (error) {
    console.error('getServerSideProps failed to complete execution on a quiz subpage');
    console.error(error);
    throw error;
  }
}

export default QuizSubpage;
